import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'TurfPulse';
const SITE_URL = 'https://getturfpulse.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const FB_PAGE = 'https://www.facebook.com/TurfPulse';

export const SEO = ({
  title,
  description,
  keywords,
  path,
  canonical,
  image = DEFAULT_IMAGE,
  imageAlt = 'TurfPulse — Smart lawn care powered by Growing Degree Days',
  type = 'website',
  noindex = false,
  article = null,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Smart Lawn Care Powered by Growing Degree Days`;
  // Canonical resolution priority:
  //   1. Explicit `canonical` (full URL) — for pages that know their absolute home
  //   2. Explicit `path` (legacy) — builds from SITE_URL + path
  //   3. Auto-detected from current route via useLocation() — self-healing default
  // ALWAYS strip query strings + hashes — Google treats `?ref=jess` as a
  // distinct URL and will flag it as duplicate without an explicit canonical.
  const location = useLocation();
  const resolvedPath = path || location.pathname || '/';
  const builtFromPath = `${SITE_URL}${resolvedPath}`;
  const canonicalUrl = (canonical || builtFromPath).split('?')[0].split('#')[0];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      {/* Always emit a robots directive so we don't rely on index.html
          defaults — keeps auth-gated routes' noindex authoritative. */}
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1'}
      />

      {/* Open Graph */}
      <meta property="og:type" content={article ? 'article' : type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Article-specific OG tags */}
      {article && <meta property="article:published_time" content={article.publishedTime} />}
      {article && <meta property="article:author" content={article.author || 'TurfPulse'} />}
      {article && article.tags?.map((tag, i) => (
        <meta key={i} property="article:tag" content={tag} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />
    </Helmet>
  );
};
